/**
 * The one place the browser talks to the server.
 *
 * Everything funnels through here so that credentials, error shapes, and
 * request tracing are handled once instead of at ninety call sites. When
 * Module 2 teaches "an API is a contract", this file is our half of it.
 */

export interface ApiErrorShape {
  code: string
  message: string
  details?: Record<string, string>
  requestId?: string
}

export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly details?: Record<string, string>
  readonly requestId?: string

  constructor(status: number, body: ApiErrorShape) {
    super(body.message)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code
    this.details = body.details
    this.requestId = body.requestId
  }
}

/** The last request/response pair, for the "Under the Hood" panel. */
export interface TrafficRecord {
  method: string
  url: string
  requestBody: unknown
  status: number
  responseBody: unknown
  ms: number
  requestId: string | null
  at: string
}

const listeners = new Set<(r: TrafficRecord) => void>()

export function onTraffic(fn: (r: TrafficRecord) => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export async function api<T = unknown>(
  path: string,
  options: { method?: string; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const method = options.method ?? 'GET'
  const url = path.startsWith('/api') ? path : `/api${path}`
  const started = performance.now()

  const res = await fetch(url, {
    method,
    headers: options.body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    // Without this, the browser will not send our session cookie on
    // same-origin fetches initiated from JS in every configuration. Being
    // explicit costs nothing and removes a class of "works locally, breaks in
    // production" bugs.
    credentials: 'same-origin',
    signal: options.signal,
  })

  const ms = Math.round(performance.now() - started)
  const text = await res.text()
  let parsed: unknown = null
  try {
    parsed = text ? JSON.parse(text) : null
  } catch {
    parsed = text
  }

  const record: TrafficRecord = {
    method,
    url,
    requestBody: options.body ?? null,
    status: res.status,
    responseBody: parsed,
    ms,
    requestId: res.headers.get('X-Request-Id'),
    at: new Date().toISOString(),
  }
  for (const fn of listeners) fn(record)

  if (!res.ok) {
    const body = (parsed as { error?: ApiErrorShape } | null)?.error ?? {
      code: 'UNKNOWN',
      message: `Request failed with ${res.status}.`,
    }
    throw new ApiError(res.status, body)
  }

  return parsed as T
}
